import { Grid } from '@mui/material'
import { Autocomplete } from 'Components/AutoComplete'
import { TextFieldWithLabel } from 'Components/TextField'
import { useEffect, useState } from 'react'
import { type lookup } from 'core/types'
import { type ColorKey, colors, colorKeys } from 'Components/Enrollment/Colors'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import { sendGet } from 'hooks/use-fetch'
import { useParams } from 'react-router'
import { useAccountId } from 'ContextProviders/CurrentAccount'

interface EnrollmentSearchProps {
  current: EnrollmentSearchCriteria
  onChange: (newSearch: EnrollmentSearchCriteria) => void
}

export interface EnrollmentSearchCriteria {
  statuses: ColorKey[]
  schoolYear: number | undefined
  session: number | undefined
  program: number | undefined
  facility: number | undefined
  childName: string
}

interface StatusCount {
  statusID: number
  count: number
}

export function EnrollmentSearch (props: EnrollmentSearchProps): JSX.Element {
  const { serviceId } = useParams()
  const accountId = useAccountId()?.id ?? 0
  const [refresh, setRefresh] = useState(new Date())
  const [stageCountList, setStageCountList] = useState<StatusCount[]>([])
  const [schoolYearList, setSchoolYearList] = useState<lookup[]>([])
  const [programs, setPrograms] = useState<lookup[]>([])
  const [sessionList, setSessionList] = useState<lookup[]>([])
  const [facilityList, setFacilityList] = useState<lookup[]>([])

  const accountID = useAccountId()?.id ?? 0

  useEffect(() => {
    async function fetchDataforFilter (): Promise<void> {
      if (accountID === 0) return

      const { response: sessions } = await sendGet(`/EnrollmentSearch/GetSessionList?id=${accountID}`, {})
      setSessionList(sessions)
      const { response } = await sendGet(`/EnrollmentSearch/GetProviderPrograms?id=${accountID}`, {})
      if (response !== null) {
        setPrograms(response)
      }
      const { response: facilities } = await sendGet(`/EnrollmentSearch/GetFacilities?id=${accountID}`, {})
      if (facilities !== null) {
        setFacilityList(facilities)
      }
      const { response: schoolYearList } = await sendGet(`/EnrollmentSearch/GetSchoolYears?id=${accountID}`, {})
      if (schoolYearList !== null) {
        setSchoolYearList(schoolYearList)
      }
    }
    void fetchDataforFilter()
  }, [accountID])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      let url = `/NewEnrollment/GetEnrollmentCountsByStatus?serviceID=${serviceId ?? 0}&spId=${accountId}`
      if (props.current.childName != null) {
        url += `&childName=${props.current.childName}`
      }
      if (props.current.schoolYear !== undefined) {
        url += `&timeSpanId=${props.current.schoolYear}`
      }
      if (props.current.session !== undefined) {
        url += `&sessionId=${props.current.session}`
      }
      if (props.current.program !== undefined) {
        url += `&programId=${props.current.program}`
      }
      if (props.current.facility !== undefined) {
        url += `&facilityId=${props.current.facility}`
      }
      const { response } = await sendGet(url, {})

      setStageCountList(response)
    }

    void fetchData()
  }, [refresh])

  const handleChangeAutocomplete = (value: number | string | undefined, name: string): void => {
    props.onChange({ ...props.current, [name]: value })
    setRefresh(new Date())
  }

  const handleChange = (e: any): void => {
    props.onChange({ ...props.current, [e.target.name]: e.target.value })
    setRefresh(new Date())
  }
  const toggleAll = (): void => {
    const newStatuses = props.current.statuses.length === colorKeys.length ? [] : colorKeys.map(c => { return c })
    props.onChange({ ...props.current, statuses: newStatuses })
  }

  const statusChanged = (newVal: boolean, name: ColorKey): void => {
    const newStatuses = newVal
      ? [...props.current.statuses, name]
      : props.current.statuses.filter((status) => status !== name)
    props.onChange({ ...props.current, statuses: newStatuses })
  }

  return <><Grid item sm={8} md={10} lg={12} container spacing={1} rowGap={0} rowSpacing={-1} >
    <Grid item>
      <TextFieldWithLabel
        label='Child Name'
        data-testid="child"
        name='childName'
        value={props.current.childName}
        onChange={handleChange}
        className='width-275'
      />
    </Grid>
    <Grid item>
      <Autocomplete
        label='School Year'
        name='schoolYear'
        onChange={(e, value) => { handleChangeAutocomplete(value?.id, 'schoolYear') }}
        options={schoolYearList}
        width='150px'
        value={String(props.current.schoolYear)}
        dataTestId='schoolYears'
      />
    </Grid>
    <Grid item>
      <Autocomplete
        label='Facility'
        name='facility'
        onChange={(e, value) => { handleChangeAutocomplete(value?.id, 'facility') }}
        options={facilityList}
        width='220px'
        value={String(props.current.facility)}
        dataTestId='facilities'
      />
    </Grid>
    <Grid item>
      <Autocomplete
        label='Program'
        name='program'
        onChange={(e, value) => { handleChangeAutocomplete(value?.id, 'program') }}
        options={programs}
        width='160px'
        value={String(props.current.program)}
        dataTestId='programs'
      />
    </Grid>
    <Grid item>
      <Autocomplete
        label='Session'
        name='session'
        onChange={(e, value) => { handleChangeAutocomplete(value?.id, 'session') }}
        options={sessionList}
        width='220px'
        value={String(props.current.session)}
        dataTestId='sessions'
      />
    </Grid>
    <Grid item sx={{ flexWrap: 'wrap', width: '100%' }}>
      <Grid container direction='row' >
        <Grid item onClick={() => {
          toggleAll()
        }} flexBasis={0} flex='1 1 0'
        >
          <CheckBoxWithLabel name={''}
            fillAllAvailable
            value={props.current.statuses.length === colorKeys.length}
            buttonClassName='state-checkbox'
            labelSx={{ marginTop: '0', height: '100%', width: '100%' }}
            textClassName='no-horiz-padding'
            checkboxClassName='no-horiz-padding'
            setValue={function (newVal: boolean): void {
            }}
            edge={'start'}
            dataTestId={'status-all'}
            label={`All (${stageCountList?.reduce((a, c) => a + c.count, 0) ?? 0})`}
          />

        </Grid>
        {Object.entries(colors).map((item, index) => (
          index !== 0 && <Grid item flexBasis={0} flex='1 1 0' sx={{ backgroundColor: item[1].fill, color: item[1].textColor }}>
            <CheckBoxWithLabel name={''}
              fillAllAvailable
              value={props.current.statuses.filter(s => s === colorKeys[index]).length !== 0}
              buttonClassName='state-checkbox'
              labelSx={{ marginTop: '0', height: '28px' }}
              textClassName='no-horiz-padding'
              checkboxClassName='no-horiz-padding'
              setValue={function (newVal: boolean): void {
                statusChanged(newVal, colorKeys[index])
              }}
              edge={'start'}
              dataTestId={`status-${colorKeys[index]}`}
              label={`${item[1].text} (${stageCountList.find(s => s.statusID === index)?.count ?? 0})`}
            />

          </Grid>
        ))}
      </Grid>
    </Grid>
  </Grid >

  </>
}
